import React, { useMemo, useState } from "react";
import moment from "moment";

import Icon from "../../../components/Icon";
import { convertIntToMoney } from "../../../helpers/conversions";

const formatMonth = month => moment(month, 'YYYY-MM').locale('pt-br').format('MMM/YYYY');

const collectGroupIds = nodes => nodes.reduce((ids, node) => (
    node.children?.length ? [...ids, node.id, ...collectGroupIds(node.children)] : ids
), []);

const sumValues = values => (values ?? []).reduce((total, value) => total + value.amount, 0);

const formatPercent = value => `${value.toFixed(2).replace('.', ',')}%`;

/**
 * DRE gerencial: plano de contas nas linhas, meses nas colunas, e a última
 * coluna fechando o período inteiro. Os valores chegam do backend já com sinal
 * (despesa negativa), então receitas + despesas = resultado se lê na vertical.
 *
 * A análise vertical fica só na coluna do período: repetida mês a mês ela
 * dobraria a largura da tabela para responder uma pergunta que é do conjunto,
 * não de cada mês.
 */
export const IncomeStatement = ({ data, loading }) => {
    const months = data?.months ?? [];

    const [expanded, setExpanded] = useState([]);

    const groupIds = useMemo(() => collectGroupIds([
        ...(data?.revenue?.categories ?? []),
        ...(data?.expense?.categories ?? []),
    ]), [data]);

    const allExpanded = groupIds.length > 0 && groupIds.every(id => expanded.includes(id));

    const toggle = id => setExpanded(current => (
        current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    ));

    const columnCount = 2 + months.length;

    const periodRevenue = data?.period?.revenue ?? 0;

    const verticalAnalysis = total => (
        periodRevenue === 0 ? null : formatPercent(total / periodRevenue * 100)
    );

    const renderValues = (values, key) => {
        const total = sumValues(values);

        return (
            <>
                {months.map((month, index) => (
                    <td key={`${key}-${month}`}>{convertIntToMoney(values?.[index]?.amount ?? 0)}</td>
                ))}
                <td className="cell-period">
                    {convertIntToMoney(total)}
                    {verticalAnalysis(total) &&
                        <span className="cell-share">{verticalAnalysis(total)}</span>
                    }
                </td>
            </>
        );
    };

    const renderNodes = (nodes, side, depth = 0) => nodes.flatMap(node => {
        const hasChildren = node.children?.length > 0;
        const isOpen = expanded.includes(node.id);
        const key = node.id ?? `${side}-uncategorized`;

        const row = (
            <tr key={key} className={`matrix-row matrix-row--${side}${hasChildren ? ' is-group' : ''}`}>
                <th scope="row" className="matrix-label" style={{ paddingLeft: 20 + (depth * 22) }}>
                    {hasChildren
                        ? <button type="button" className="matrix-toggle" onClick={() => toggle(node.id)}>
                            <Icon name={isOpen ? 'arrow-dropdown' : 'arrow'} size={18} />
                        </button>
                        : <span className="matrix-toggle-placeholder" />
                    }
                    {node.code ? <span className="matrix-code">{node.code}</span> : null}
                    {node.name}
                </th>
                {renderValues(node.values, key)}
            </tr>
        );

        return hasChildren && isOpen ? [row, ...renderNodes(node.children, side, depth + 1)] : [row];
    });

    if (!loading && months.length === 0) {
        return <div className="income-statement-empty">Nenhum resultado encontrado</div>;
    }

    return (
        <div className="income-statement">
            <div className="matrix-toolbar">
                <button type="button" className="matrix-expand-all" onClick={() => setExpanded(allExpanded ? [] : groupIds)}>
                    {allExpanded ? 'Recolher tudo' : 'Expandir tudo'}
                </button>
            </div>

            <div className="matrix-scroll">
                <table>
                    <thead>
                        <tr className="matrix-months">
                            <th className="matrix-label matrix-corner">Categoria</th>
                            {months.map(month => (
                                <th key={month} className="matrix-month">{formatMonth(month)}</th>
                            ))}
                            <th className="matrix-month cell-period">Período</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading &&
                            <tr>
                                <td className="matrix-loading" colSpan={columnCount}>Carregando...</td>
                            </tr>
                        }

                        {!loading && <>
                            <tr className="matrix-row matrix-row--total matrix-row--revenue">
                                <th scope="row" className="matrix-label">Receitas</th>
                                {renderValues(data.revenue.total, 'revenue-total')}
                            </tr>
                            {renderNodes(data.revenue.categories, 'revenue')}

                            <tr className="matrix-row matrix-row--total matrix-row--expense">
                                <th scope="row" className="matrix-label">Despesas</th>
                                {renderValues(data.expense.total, 'expense-total')}
                            </tr>
                            {renderNodes(data.expense.categories, 'expense')}

                            <tr className="matrix-row matrix-row--result">
                                <th scope="row" className="matrix-label">Resultado</th>
                                {months.map((month, index) => (
                                    <td key={`result-${month}`} className={data.result[index].amount < 0 ? 'value-negative' : ''}>
                                        {convertIntToMoney(data.result[index].amount)}
                                    </td>
                                ))}
                                <td className={`cell-period${data.period.result < 0 ? ' value-negative' : ''}`}>
                                    {convertIntToMoney(data.period.result)}
                                </td>
                            </tr>

                            <tr className="matrix-row matrix-row--margin">
                                <th scope="row" className="matrix-label">Margem</th>
                                {months.map((month, index) => (
                                    <td key={`margin-${month}`} className={data.result[index].margin < 0 ? 'value-negative' : ''}>
                                        {data.result[index].margin === null ? '—' : formatPercent(data.result[index].margin)}
                                    </td>
                                ))}
                                <td className={`cell-period${data.period.margin < 0 ? ' value-negative' : ''}`}>
                                    {data.period.margin === null ? '—' : formatPercent(data.period.margin)}
                                </td>
                            </tr>
                        </>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default IncomeStatement;
